import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  Theme,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import arrayMutators from "final-form-arrays";
import React, { useMemo } from "react";
import { FormProps, withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import { SaveResourceLibraryItemProfessionRequest } from "services/KnowfullyAdminClient";
import SelectProfession from "components/SelectProfession";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  subtitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  checkboxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  lineContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  info: {
    flexGrow: 1,
    textAlign: "right",
    margin: theme.spacing(0, 2),
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
}));

const { Form } = withTypes<SaveResourceLibraryItemProfessionRequest>();

type ResourceLibraryItemProfessionFormProps = {
  submitLabel: string;
  onSubmit: (values: SaveResourceLibraryItemProfessionRequest) => void;
  onCancel?: () => void;
};

const ResourceLibraryItemProfessionForm = ({
  submitLabel,
  onCancel,
  onSubmit,
}: ResourceLibraryItemProfessionFormProps &
  FormProps<SaveResourceLibraryItemProfessionRequest>) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());

  return useMemo(
    () => (
      <Form
        onSubmit={onSubmit}
        mutators={{ ...arrayMutators }}
        render={({ handleSubmit, submitting, submitError }) => {
          return (
            <form onSubmit={handleSubmit} noValidate>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Field name="professionId" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectProfession
                            {...input}
                            label={t({
                              id: "professionLabel",
                            })}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                            required
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  {submitError && (
                    <Alert sx={styles.feedback} severity="error">
                      {submitError}
                    </Alert>
                  )}
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button disabled={submitting} onClick={onCancel} data-cy="cancelButton">
                  {t({ id: "cancelButton" })}
                </Button>

                <Button type="submit" color="primary" disabled={submitting} data-cy="saveButton">
                  {submitLabel}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      />
    ),
    [styles.feedback, onCancel, onSubmit, submitLabel, t]
  );
};

export default ResourceLibraryItemProfessionForm;
